import { useState } from "react";
import { Select, InputStyledDiv, Close, Info } from "components";
import { TableFilter, TableHeading, TableOptions } from "components/Table";
import { useAdminManagementContext } from "pages/AdminManagement/utils/AdminManagementContext";
import { useGetCompaniesQuery } from "services";
import { STATUSES, FILTER_USER_TYPE, isMasterAdmin, transformCompanyToOptions } from "helpers";
import { t } from "i18next";
import { SearchableSelect, OptionProps } from "components/forms/SearchableSelect";
import { twMerge } from "tailwind-merge";

export const AdminTableHeadings = () => {
  const {
    user,
    companyId: selectedCompany,
    params,
    onResetFilters,
    onSort,
    onFilterByStatus,
    onFilterByUserType,
    onChangeCompany,
  } = useAdminManagementContext();

  const {
    data: companiesData,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useGetCompaniesQuery({
    page: 0,
    pageSize: 100,
  });

  const [showInfo, setShowInfo] = useState<boolean>(false);

  const resetFilters = (
    <div className="absolute -mt-1.5 flex flex-col">
      {showInfo && (
        <InputStyledDiv className="absolute -mt-4 ml-6 w-max bg-primary/5 p-0 text-xs font-thin">
          {t("adminManagement.clearAllFilters")}
        </InputStyledDiv>
      )}
      <div onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)} className="relative">
        <Info width={7} height={7} className="ml-4" />
      </div>
      <Close className="text-error-text" onClick={onResetFilters} />
    </div>
  );

  const companyOptions = (() => {
    if (user && !isMasterAdmin(user.type.id)) {
      return [
        {
          value: "",
          label: t("adminManagement.allCompanies"),
        },
        {
          value: user.company_id || "0",
          label: companiesData?.companies?.find((c) => c.company_id === user.company_id)?.name || t("adminManagement.currentCompany"),
        },
      ] as OptionProps[];
    }

    const allCompanies = companiesData?.companies ? transformCompanyToOptions(companiesData.companies) : [];

    return [
      {
        value: "",
        label: t("adminManagement.allCompanies"),
      },
      ...allCompanies,
    ];
  })();

  const headings: Array<JSX.Element | string> = [
    <>
      {resetFilters}
      <TableHeading onClick={(order) => onSort("full-name", order)} order={params.order || "DESC"}>
        {t("adminManagement.fullName")}
      </TableHeading>
    </>,
    isCompaniesLoading ? (
      <div>{t("adminManagement.loadingCompanies")}</div>
    ) : isCompaniesError ? (
      <div>{t("adminManagement.errorLoadingCompanies")}</div>
    ) : (
      <SearchableSelect
        options={companyOptions}
        placeholder={t("adminManagement.selectCompany")}
        onChange={onChangeCompany}
        value={user?.company_id}
        disabled={user && !isMasterAdmin(user.type.id) && !user.company?.name}
      />
    ),
    t("adminManagement.phoneNumberAndEmail"),
    <TableHeading onClick={(order) => onSort("last-login", order)}>{t("adminManagement.lastSignIn")}</TableHeading>,
    <TableFilter onFilter={onFilterByStatus} values={STATUSES} label={t("adminManagement.filterByStatus")} selectedFilter={params.status} />,
    <TableFilter
      onFilter={onFilterByUserType}
      values={FILTER_USER_TYPE}
      label={t("adminManagement.filterByType")}
      selectedFilter={params.type}
    />,
    <TableOptions>{t("adminManagement.options")}</TableOptions>,
  ];

  return (
    <>
      {headings.map((heading, index) => {
        const isLast = index === headings.length - 1;

        return (
          <th
            key={index}
            scope="col"
            className={twMerge(
              "h-[100px] max-h-[100px] w-[200px] min-w-[200px] break-words bg-white py-4 text-sm font-semibold text-primary",
              isLast && "shadow-left sticky right-0 z-10"
            )}
          >
            {heading}
          </th>
        );
      })}
    </>
  );
};

export default AdminTableHeadings;
